import React from 'react';
import Modal from 'src/components/common/ui/Modal';
import type { WeeklyNarrative } from 'src/features/dashboard/api/reports.api';

function Section({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
    return (
        <div className="rounded-2xl border bg-white p-4 shadow-sm">
            <div className="mb-3">
                <div className="text-sm font-semibold text-gray-900">{title}</div>
                {subtitle ? <div className="mt-0.5 text-xs text-gray-500">{subtitle}</div> : null}
            </div>
            {children}
        </div>
    );
}

function TextArea({
    value,
    onChange,
    placeholder,
    disabled,
    rows = 7,
}: {
    value: string;
    onChange: (v: string) => void;
    placeholder?: string;
    disabled: boolean;
    rows?: number;
}) {
    return (
        <textarea
            rows={rows}
            className={[
                'w-full resize-y rounded-xl border px-3 py-2 text-sm leading-6 outline-none',
                'bg-white focus:ring-2 focus:ring-black/10',
                disabled ? 'opacity-70 cursor-not-allowed bg-gray-50' : '',
            ].join(' ')}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            disabled={disabled}
        />
    );
}

function ActionRow({
    idx,
    value,
    onChange,
    onRemove,
    disabled,
}: {
    idx: number;
    value: string;
    onChange: (v: string) => void;
    onRemove: () => void;
    disabled: boolean;
}) {
    return (
        <div className="flex items-start gap-2">
            <div className="mt-2 w-7 text-right text-xs font-medium text-gray-500">{idx + 1}.</div>
            <input
                className={[
                    'flex-1 rounded-xl border px-3 py-2 text-sm outline-none',
                    'bg-white focus:ring-2 focus:ring-black/10',
                    disabled ? 'opacity-70 cursor-not-allowed bg-gray-50' : '',
                ].join(' ')}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                disabled={disabled}
                placeholder="예: Basic Design 승인 일정 재확정(담당/기한)…"
            />
            <button
                type="button"
                onClick={onRemove}
                disabled={disabled}
                className={[
                    'rounded-xl border px-3 py-2 text-xs font-medium',
                    'hover:bg-gray-50 active:scale-[0.99] transition',
                    disabled ? 'opacity-60 cursor-not-allowed' : '',
                ].join(' ')}
                title="삭제"
            >
                삭제
            </button>
        </div>
    );
}

export default function WeeklyNarrativeEditModal({
    open,
    onClose,
    loading,
    narrative,
    onChange,
    onRegenerate,
    onGeneratePdf,
    busy,
    errorMessage,
}: {
    open: boolean;
    onClose: () => void;
    loading: boolean;
    narrative: WeeklyNarrative | null;
    onChange: (next: WeeklyNarrative) => void;
    onRegenerate: () => void;
    onGeneratePdf: () => void;
    busy: boolean;
    errorMessage?: string | null;
}) {
    const disabled = loading || busy;

    const body = loading ? (
        <div className="flex items-center gap-3 text-sm">
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-700 border-t-transparent" />
            <div>AI 코멘트를 불러오는 중...생성되는 내용에 따라 10초 가량 걸릴 수 있습니다.</div>
        </div>
    ) : !narrative ? (
        <div className="text-sm text-gray-600">코멘트를 불러오지 못했습니다.</div>
    ) : (
        <div className="space-y-4">
            <div className="rounded-2xl bg-amber-50 px-4 py-3 text-xs text-amber-900 border border-amber-100">
                팁) AI가 생성한 코멘트를 수정할 수 있습니다. 여기서 수정한 문구가 보고서(PDF)에 그대로 반영됩니다.
            </div>
            {errorMessage ? (
                <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                    {errorMessage}
                </div>
            ) : null}

            <Section title="Executive Summary" subtitle="이번 주 진행/성과를 5~8문장 정도로 정리">
                <TextArea
                    value={narrative.executiveSummary}
                    onChange={(v) => onChange({ ...narrative, executiveSummary: v })}
                    placeholder="예: 금주 EV는 계획 대비 +2.1%p로 회복 추세…"
                    disabled={disabled}
                    rows={6} // ✅ 더 넓게
                />
            </Section>

            <Section title="Risk Comment" subtitle="핵심 리스크와 대응 방향을 3~6문장으로 정리">
                <TextArea
                    value={narrative.riskComment}
                    onChange={(v) => onChange({ ...narrative, riskComment: v })}
                    placeholder="예: 주요 리스크는 ○○이며, 대응은…"
                    disabled={disabled}
                    rows={6} // ✅ 더 넓게
                />
            </Section>

            <Section title="Next Actions" subtitle="AI가 제안한 액션을 검토하고 필요하면 삭제/추가할 수 있습니다.">
                <div className="flex items-center justify-between">
                    <div className="text-xs text-gray-500">
                        {Math.max((narrative.nextActions ?? []).length, 0)} items
                    </div>
                    <button
                        type="button"
                        disabled={!narrative || disabled}
                        className={[
                            'rounded-xl bg-black px-3 py-2 text-xs font-medium text-white',
                            'hover:bg-black/90 active:scale-[0.99] transition',
                            disabled ? 'opacity-60 cursor-not-allowed' : '',
                        ].join(' ')}
                        onClick={() => {
                            if (!narrative) return;
                            onChange({
                                ...narrative,
                                nextActions: [...(narrative.nextActions ?? []), ''],
                            });
                        }}
                    >
                        + 추가
                    </button>
                </div>

                <div className="mt-3 grid gap-2">
                    {(narrative?.nextActions ?? []).length === 0 ? (
                        <div className="text-xs text-gray-500">
                            추가된 액션이 없습니다. 오른쪽 “+ 추가”로 입력할 수 있습니다.
                        </div>
                    ) : (
                        (narrative?.nextActions ?? []).map((v, i) => (
                            <ActionRow
                                key={i}
                                idx={i}
                                value={v}
                                disabled={disabled}
                                onChange={(next) => {
                                    if (!narrative) return;
                                    const arr = [...(narrative.nextActions ?? [])];
                                    arr[i] = next;
                                    onChange({ ...narrative, nextActions: arr });
                                }}
                                onRemove={() => {
                                    if (!narrative) return;
                                    const arr = [...(narrative.nextActions ?? [])];
                                    arr.splice(i, 1);
                                    onChange({ ...narrative, nextActions: arr });
                                }}
                            />
                        ))
                    )}
                </div>
            </Section>
        </div>
    );

    const footer = (
        <div className="flex items-center justify-between gap-2">
            <button
                type="button"
                onClick={onRegenerate}
                disabled={disabled}
                className={[
                    'rounded-xl border px-3 py-2 text-sm font-medium',
                    'hover:bg-gray-50 active:scale-[0.99] transition',
                    disabled ? 'opacity-60 cursor-not-allowed' : '',
                ].join(' ')}
            >
                AI 다시 생성
            </button>

            <div className="flex items-center gap-2">
                <button
                    type="button"
                    onClick={onClose}
                    disabled={busy}
                    className={[
                        'rounded-xl px-3 py-2 text-sm font-medium',
                        'hover:bg-gray-50 active:scale-[0.99] transition',
                        busy ? 'opacity-60 cursor-not-allowed' : '',
                    ].join(' ')}
                >
                    취소
                </button>
                <button
                    type="button"
                    onClick={onGeneratePdf}
                    disabled={loading || busy || !narrative}
                    className={[
                        'rounded-xl bg-black px-3 py-2 text-sm font-medium text-white',
                        'hover:bg-black/90 active:scale-[0.99] transition',
                        loading || busy || !narrative ? 'opacity-60 cursor-not-allowed' : '',
                    ].join(' ')}
                >
                    PDF 생성
                </button>
            </div>
        </div>
    );

    return (
        <Modal
            open={open}
            onClose={onClose}
            title="주간 보고서 코멘트 편집"
            size="5xl"
            footer={footer}
            // closeOnBackdrop={!busy}
            // closeOnEsc={!busy}
            closeOnBackdrop={false}
            closeOnEsc={false}
        >
            {body}
        </Modal>
    );
}
